/**
 * CountryFlag — a small flag beside a country code, because "which country" is the first thing
 * the Technical Delegate reads on an official, a team, or a person in a hearing.
 *
 * Real IOC codes map to ISO-3166 alpha-2 and render as the regional-indicator emoji flag.
 * Codes with no mapping (the demonstration world is wholly fictional) render a deterministic
 * two-tone badge carrying the code, so the demo looks consistent without inventing a nation's flag.
 * The code itself is always printed beside the flag — the flag is an aid, never the only signal.
 */
import type React from "react";

/** IOC → ISO-3166 alpha-2, for the codes that differ or that we expect at a continental event. */
const IOC_TO_ISO: Readonly<Record<string, string>> = {
  AFG: "AF", AUS: "AU", BAN: "BD", BHU: "BT", BRN: "BH", BRU: "BN", CAM: "KH", CHN: "CN",
  HKG: "HK", INA: "ID", IND: "IN", IRI: "IR", IRQ: "IQ", JOR: "JO", JPN: "JP", KAZ: "KZ",
  KGZ: "KG", KOR: "KR", KSA: "SA", KUW: "KW", LAO: "LA", LBN: "LB", MAC: "MO", MAS: "MY",
  MDV: "MV", MGL: "MN", MYA: "MM", NEP: "NP", NZL: "NZ", OMA: "OM", PAK: "PK", PHI: "PH",
  PLE: "PS", PRK: "KP", QAT: "QA", SGP: "SG", SRI: "LK", SYR: "SY", THA: "TH", TJK: "TJ",
  TKM: "TM", TLS: "TL", TPE: "TW", UAE: "AE", UZB: "UZ", VIE: "VN", YEM: "YE",
  ARG: "AR", AUT: "AT", BEL: "BE", CAN: "CA", ESP: "ES", FRA: "FR", GBR: "GB", GER: "DE",
  IRL: "IE", ITA: "IT", NED: "NL", POL: "PL", RSA: "ZA", SUI: "CH", USA: "US", WAL: "GB",
  ENG: "GB", SCO: "GB", CHI: "CL", EGY: "EG", NGR: "NG", KEN: "KE", ZIM: "ZW",
};

function isoToEmoji(iso: string): string {
  return Array.from(iso.toUpperCase())
    .map((ch) => String.fromCodePoint(0x1f1e6 + ch.charCodeAt(0) - 65))
    .join("");
}

/** Deterministic hue pair from the code, so a fictional nation keeps its colours everywhere. */
function badgeColours(code: string): { a: string; b: string } {
  let h = 0;
  for (const ch of code) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return { a: `hsl(${h} 55% 38%)`, b: `hsl(${(h + 140) % 360} 60% 55%)` };
}

export interface CountryFlagProps {
  /** IOC three-letter code, e.g. "JPN"; null/undefined renders nothing. */
  readonly code: string | null | undefined;
  /** Also print the code after the flag (default true). */
  readonly withCode?: boolean;
  readonly className?: string;
}

export function CountryFlag({ code, withCode = true, className }: CountryFlagProps): React.JSX.Element | null {
  if (code === null || code === undefined || code.trim() === "") return null;
  const ioc = code.trim().toUpperCase();
  const iso = IOC_TO_ISO[ioc];
  const classes = ["cflag", className ?? ""].filter((c) => c !== "").join(" ");
  if (iso !== undefined) {
    return (
      <span className={classes} data-country={ioc}>
        <span className="cflag__emoji" role="img" aria-label={ioc}>
          {isoToEmoji(iso)}
        </span>
        {withCode ? <span className="cflag__code num">{ioc}</span> : null}
      </span>
    );
  }
  const { a, b } = badgeColours(ioc);
  return (
    <span className={classes} data-country={ioc}>
      <span
        className="cflag__badge"
        role="img"
        aria-label={ioc}
        style={{ background: `linear-gradient(135deg, ${a} 0 50%, ${b} 50% 100%)` }}
      />
      {withCode ? <span className="cflag__code num">{ioc}</span> : null}
    </span>
  );
}
